/**
 * Turning an approved quote into a purchase order.
 *
 * The stage exists for how a machine shop actually buys: the quote goes up
 * their approval ladder, their own ERP issues a PO number, and their accounts
 * team will only pay an invoice that quotes it back. So this asks for that
 * number and takes the PO document their system produced, and we hold both
 * against our own reference.
 *
 * What is asked for, what is compulsory and what file types we take all come
 * from Tools > Customer Order Flow w ERP; the endpoint enforces the same rules.
 */
import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import {
  FileUpload,
  PurchaseOrder,
  Quote,
  QuoteFlowConfig,
  messageOf,
  raisePurchaseOrder,
  readFileForUpload,
} from "./api";

export type ConvertToPoDialogProps = {
  open: boolean;
  onClose: () => void;
  quote: Quote;
  config: QuoteFlowConfig;
  signedIn: boolean;
  onRaised: (po: PurchaseOrder) => void;
};

const field =
  "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#00062e] focus:outline-none";

const money = (amount: number, currency: string) =>
  `${currency === "USD" ? "$" : `${currency} `}${amount.toLocaleString(
    "en-US",
    { minimumFractionDigits: 2, maximumFractionDigits: 2 },
  )}`;

export default function ConvertToPoDialog({
  open,
  onClose,
  quote,
  config,
  signedIn,
  onRaised,
}: ConvertToPoDialogProps) {
  const po = config.po;
  const [customerPoNumber, setCustomerPoNumber] = useState("");
  const [file, setFile] = useState<FileUpload | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setCustomerPoNumber("");
    setFile(null);
    setError("");
  }, [open]);

  const needsSignIn = po.requireSignIn && !signedIn;
  const accept = po.uploadFileTypes.map((type) => `.${type}`).join(",");

  const pick = async (chosen: File | null) => {
    setError("");
    if (!chosen) {
      setFile(null);
      return;
    }
    if (chosen.size > po.uploadMaxMb * 1024 * 1024) {
      setError(
        `That file is ${(chosen.size / (1024 * 1024)).toFixed(
          1,
        )} MB and the limit is ${po.uploadMaxMb} MB.`,
      );
      return;
    }
    try {
      setFile(await readFileForUpload(chosen));
    } catch (e) {
      setError(messageOf(e));
    }
  };

  const submit = async () => {
    setError("");
    setBusy(true);
    try {
      onRaised(
        await raisePurchaseOrder({
          quoteId: quote.id,
          customerPoNumber,
          attachment: file,
        }),
      );
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-2 sm:scale-95"
            >
              <Dialog.Panel className="w-full max-w-xl transform rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-start justify-between">
                  <Dialog.Title className="text-lg font-bold text-[#00062e]">
                    Convert {quote.reference} to a purchase order
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                    aria-label="Close"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mt-4 text-sm">
                  <p className="text-gray-600">
                    This holds the quoted lines and the quoted price of{" "}
                    <span className="font-medium">
                      {money(
                        quote.totals.totalPrice,
                        quote.totals.currency || "USD",
                      )}
                    </span>{" "}
                    as an order under our own reference, which we will quote on
                    the invoice. Payment comes next, and nothing is charged
                    until you choose how to pay.
                  </p>

                  {needsSignIn && (
                    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-gray-700">
                      A purchase order is held against an account.{" "}
                      <Link
                        className="font-medium text-blue-700 hover:underline"
                        to="/signin"
                      >
                        Sign in
                      </Link>{" "}
                      to raise one.
                    </div>
                  )}

                  {po.askCustomerPoNumber && (
                    <label className="mt-4 block">
                      <span className="text-xs text-gray-600">
                        Your own PO number
                        {po.requireCustomerPoNumber ? "" : " (optional)"}
                      </span>
                      <input
                        className={field}
                        name="customer-po-number"
                        value={customerPoNumber}
                        onChange={(e) => setCustomerPoNumber(e.target.value)}
                        placeholder="e.g. 4500123987"
                      />
                      <span className="mt-1 block text-xs text-gray-500">
                        The number your own system issued. We print it on the
                        invoice so your accounts team can match it.
                      </span>
                    </label>
                  )}

                  {po.allowPoUpload && (
                    <label className="mt-4 block">
                      <span className="text-xs text-gray-600">
                        Your PO document
                        {po.requirePoUpload ? "" : " (optional)"}
                      </span>
                      <input
                        className="mt-1 block w-full text-sm text-gray-700 file:mr-3 file:rounded-full file:border-0 file:bg-gray-100 file:px-4 file:py-2 file:text-sm hover:file:bg-gray-200"
                        type="file"
                        name="customer-po-file"
                        accept={accept}
                        onChange={(e) => void pick(e.target.files?.[0] || null)}
                      />
                      <span className="mt-1 block text-xs text-gray-500">
                        {po.uploadFileTypes
                          .map((type) => type.toUpperCase())
                          .join(", ")}{" "}
                        up to {po.uploadMaxMb} MB. It is filed against this
                        order and sent to the team handling it.
                      </span>
                    </label>
                  )}

                  {error && <p className="mt-3 text-red-600">{error}</p>}

                  <div className="mt-5 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      className="rounded-full bg-[#00062e] px-4 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:opacity-50"
                      onClick={() => void submit()}
                      disabled={busy || needsSignIn}
                    >
                      {busy ? "Raising it..." : "Raise the purchase order"}
                    </button>
                    <button
                      type="button"
                      className="text-sm text-gray-600 hover:underline"
                      onClick={onClose}
                    >
                      Not yet
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
